import { Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { catchError, of } from 'rxjs';
import { Allocation, InvoiceType } from '../../models/allocation.model';
import { AllocationService } from '../../services/allocation.service';
import { AuthService } from '../../services/auth.service';

/**
 * Read-only monthly team allocation for a single project.
 *
 * Shared by BOTH the delivery Project Details dialog and the Presales Project Details dialog —
 * the presales side passes its twin project id (PresalesProject.promotedToProjectId), since
 * allocations are always stored against a Projects row.
 *
 * Read-only by design: allocations are created and edited on the Allocation Plan page, which
 * owns the month open/closed rules and the available-capacity checks. The "Manage" button
 * hands off to that page rather than duplicating any of it here.
 */
@Component({
  selector: 'app-team-allocation-month',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, Button, Select, Tooltip],
  templateUrl: './team-allocation-month.component.html',
  styleUrl: './team-allocation-month.component.scss',
})
export class TeamAllocationMonthComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);

  /** Projects row id. For presales, the twin id (promotedToProjectId). */
  @Input() projectId: string = '';

  /** Shown in the handoff button label so it reads the same on both dialogs. */
  @Input() projectName: string = '';

  allocations: Allocation[] = [];
  loading = false;

  selectedMonth: number;
  selectedYear: number;

  readonly monthOptions = [
    { label: 'January', value: 1 },   { label: 'February', value: 2 },
    { label: 'March', value: 3 },     { label: 'April', value: 4 },
    { label: 'May', value: 5 },       { label: 'June', value: 6 },
    { label: 'July', value: 7 },      { label: 'August', value: 8 },
    { label: 'September', value: 9 }, { label: 'October', value: 10 },
    { label: 'November', value: 11 }, { label: 'December', value: 12 },
  ];

  /** Current year +/- 2 — matches the range the Allocation Plan page offers. */
  readonly yearOptions: { label: string; value: number }[];

  constructor(
    private allocationService: AllocationService,
    private authService: AuthService,
    private router: Router,
  ) {
    const now = new Date();
    this.selectedMonth = now.getMonth() + 1;
    this.selectedYear = now.getFullYear();

    const base = now.getFullYear();
    this.yearOptions = [base - 2, base - 1, base, base + 1, base + 2]
      .map(y => ({ label: String(y), value: y }));
  }

  /**
   * Mirrors canAddAllocation() on the Allocation Plan page. Sales can read allocations but
   * not create them, so they get the table without a button that leads nowhere.
   */
  get canManageAllocation(): boolean {
    return this.authService.hasPMAccess();
  }

  ngOnInit(): void {
    // Deliberately no load here: Angular runs ngOnChanges before ngOnInit, so the initial
    // projectId bind has already triggered load(). Loading again would double every request.
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) this.load();
  }

  onPeriodChange(): void {
    this.load();
  }

  load(): void {
    if (!this.projectId) {
      this.allocations = [];
      return;
    }

    this.loading = true;
    this.allocationService
      .getProjectAllocations(this.projectId, this.selectedYear, this.selectedMonth)
      .pipe(
        // A project with no allocations in the chosen month is the normal empty case,
        // not an error worth a toast inside a details dialog.
        catchError(() => of([] as Allocation[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(data => {
        this.allocations = data ?? [];
        this.loading = false;
      });
  }

  /** The five week columns, for iterating header/total cells without repeating markup. */
  readonly weeks = [1, 2, 3, 4, 5] as const;

  /** Column total for one week across everyone on the project. */
  weekTotal(week: number): number {
    const key = `week${week}Percentage` as
      'week1Percentage' | 'week2Percentage' | 'week3Percentage' | 'week4Percentage' | 'week5Percentage';
    return this.allocations.reduce((sum, a) => sum + (Number(a[key]) || 0), 0);
  }

  // ---- Presentation helpers, mirrored from the Allocation Plan grid so both tables render
  // identically. Kept as local copies rather than shared: the grid's versions live on a
  // 2000-line component whose state this tab does not want.

  getEmployeeInitials(name: string | null | undefined): string {
    if (!name || !name.trim()) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  }

  getInvoiceTypeLabel(invoiceType: InvoiceType): string {
    switch (invoiceType) {
      case InvoiceType.Monthly:          return 'Monthly';
      case InvoiceType.ProjectMilestone: return 'Milestone';
      case InvoiceType.NotAvailable:     return 'N/A';
      default:                           return 'Unknown';
    }
  }

  getInvoiceTypeSeverity(invoiceType: InvoiceType): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (invoiceType) {
      case InvoiceType.Monthly:          return 'success';
      case InvoiceType.ProjectMilestone: return 'info';
      case InvoiceType.NotAvailable:     return 'secondary';
      default:                           return 'contrast';
    }
  }

  /** Severity for the week bar/text: zero (amber), half, full, over (red), normal. */
  getWeekPercentSeverity(pct: number | string | null | undefined): 'empty' | 'zero' | 'half' | 'normal' | 'full' | 'over' {
    if (pct == null || pct === '') return 'empty';
    const n = Number(pct);
    if (Number.isNaN(n)) return 'empty';
    if (n === 0) return 'zero';
    if (n === 50) return 'half';
    if (n === 100) return 'full';
    if (n > 100) return 'over';
    return 'normal';
  }

  /**
   * Hand off to the Allocation Plan page, pre-filling the Add Allocation form with this
   * project and the month currently being viewed.
   */
  manageAllocation(): void {
    // Guard: an un-promoted presales pursuit has no twin project id yet, and navigating with
    // an empty projectId would strand the user on the Allocation Plan page with no dialog.
    if (!this.projectId) return;

    this.router.navigate(['/allocations'], {
      queryParams: {
        projectId: this.projectId,
        year: this.selectedYear,
        month: this.selectedMonth,
        openForm: 1,
      },
    });
  }
}
